import { twJoin } from 'tailwind-merge';

import { HeaderTextButton } from '@/components/headerItems/HeaderTextButton';
import type {
  HeaderButtonItem,
  HeaderItemRole,
  HeaderItemTone,
} from '@/components/headerItems/types';
import { Button, ButtonIcon } from '@/components/ui/button';
import { getAccessibilityProps } from '@/util/accessibilityProps';

const ICON_TONE: Record<HeaderItemRole, string> = {
  default: 'text-typography-900',
  confirm: 'text-primary-600',
  destructive: 'text-error-600',
  accent: 'text-primary-700',
};

const ON_BRAND_TONE = 'text-typography-white';

type HeaderButtonProps = {
  item: HeaderButtonItem;
  tone?: HeaderItemTone;
};

/**
 * One header action where the bar is not UIKit's: the Material top app bar on Android, and
 * the web header row.
 *
 * An item with an icon is a 48px square, the Material action size and the minimum target in
 * one, so two side by side need no extra spacing to keep presses apart. An item without one
 * is a word, drawn by the shared `HeaderTextButton`.
 *
 * iOS does not draw this. There the same item becomes a native bar button, which the system
 * styles and wraps in Liquid Glass itself.
 */
export function HeaderButton({ item, tone = 'default' }: HeaderButtonProps) {
  const role = item.role ?? 'default';

  if (!item.icon) {
    return (
      <HeaderTextButton
        label={item.label}
        onPress={item.onPress}
        role={role}
        tone={tone}
        accessibilityHint={item.accessibilityHint}
        isDisabled={item.isDisabled}
        isLoading={item.isLoading}
      />
    );
  }

  const isDisabled = Boolean(item.isDisabled) || Boolean(item.isLoading);

  return (
    <Button
      variant="link"
      className={twJoin('h-12 w-12 px-0', isDisabled && 'opacity-50')}
      onPress={item.onPress}
      disabled={isDisabled}
      {...getAccessibilityProps({
        label: item.label,
        role: 'button',
        hint: item.accessibilityHint,
      })}>
      <ButtonIcon
        as={item.icon.lucide}
        className={twJoin('h-6 w-6', tone === 'onBrand' ? ON_BRAND_TONE : ICON_TONE[role])}
      />
    </Button>
  );
}
